import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";

const SidebarContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;
`;

const LogoContainer = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  padding: 0 ${({ $collapsed }) => ($collapsed ? "0" : "24px")};
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a365d;
  transition: all 0.3s ease;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    font-size: 1.2rem;
  `}
`;

const NavigationContainer = styled.nav`
  flex: 1;
  padding: 16px 0;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const CollapseButton = styled.button`
  position: absolute;
  top: 20px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #64748b;

  &:hover {
    color: #1e293b;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const NavSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
  padding: 0 ${({ $collapsed }) => ($collapsed ? "24px" : "24px")};
  transition: all 0.3s ease;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    opacity: 0;
    height: 0;
    margin: 0;
    overflow: hidden;
  `}
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px ${({ $collapsed }) => ($collapsed ? "24px" : "24px")};
  border: none;
  background: transparent;
  color: ${({ $active }) => ($active ? "#1e40af" : "#475569")};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  position: relative;

  &:hover {
    background: #f1f5f9;
    color: #1e40af;
  }

  ${({ $active }) =>
    $active &&
    `
    background: #eff6ff;
    border-right: 3px solid #1e40af;
  `}
`;

const NavIcon = styled.span`
  font-size: 1.2rem;
  margin-right: ${({ $collapsed }) => ($collapsed ? "0" : "16px")};
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: margin 0.3s ease;
`;

const NavLabel = styled.span`
  transition: all 0.3s ease;
  white-space: nowrap;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    opacity: 0;
    width: 0;
    overflow: hidden;
  `}
`;

const QuickActionsContainer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const QuickActionButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #475569;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${({ $collapsed }) =>
    $collapsed &&
    `
    display: none;
  `}
`;

const DashboardSidebar = ({ collapsed, onToggle, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Map user role to dashboard role
  const getUserRole = () => {
    if (!user?.role) return "employee";
    
    switch(user.role) {
      case 'COMPANY_ADMIN':
      case 'ADMIN':
      case 'SUPER_ADMIN':
        return 'admin';
      case 'MANAGER':
      case 'TEAM_LEADER':
        return 'manager';
      default:
        return 'employee';
    }
  };
  
  const userRole = getUserRole();

  const primaryNavItems = [
    { id: "dashboard", label: "Dashboard", icon: "📊", path: "/dashboard" },
    {
      id: "assessments",
      label: "Avaliações",
      icon: "📋",
      path: "/avaliacoes",
    },
    { id: "okrs", label: "OKRs", icon: "🎯", path: "/okrs" },
    { id: "pdis", label: "PDIs", icon: "📈", path: "/pdi" },
    { id: "feedback", label: "Feedbacks", icon: "💬", path: "/feedback" },
    { id: "analytics", label: "Analytics", icon: "📊", path: "/analytics" },
    { id: "meetings", label: "Reuniões 1:1", icon: "👥", path: "/meetings" },
    { id: "surveys", label: "Pesquisas", icon: "📋", path: "/surveys" },
  ];

  const managementNavItems = [
    { id: "reports", label: "Relatórios", icon: "📈", path: "/reports" },
    { id: "analytics", label: "Analytics", icon: "📊", path: "/analytics" },
  ];

  const adminNavItems = [
    { id: "admin", label: "Administração", icon: "⚙️", path: "/admin" },
    { id: "users", label: "Gerenciar Usuários", icon: "👥", path: "/admin/users" },
    { id: "teams", label: "Equipes", icon: "👫", path: "/admin/teams" },
    {
      id: "settings",
      label: "Configurações",
      icon: "🔧",
      path: "/admin/settings",
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const isActiveRoute = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <SidebarContainer>
      <LogoContainer $collapsed={collapsed}>
        <Logo $collapsed={collapsed}>{collapsed ? "BU" : "Beatt Pulse"}</Logo>
        <CollapseButton onClick={onToggle}>
          {collapsed ? "→" : "←"}
        </CollapseButton>
        <CloseButton onClick={onCloseMobile}>✕</CloseButton>
      </LogoContainer>

      <NavigationContainer>
        <NavSection>
          <SectionTitle $collapsed={collapsed}>Principal</SectionTitle>
          {primaryNavItems.map((item) => (
            <NavItem key={item.id}>
              <NavLink
                $collapsed={collapsed}
                $active={isActiveRoute(item.path)}
                onClick={() => handleNavigation(item.path)}
                title={collapsed ? item.label : undefined}
              >
                <NavIcon $collapsed={collapsed}>{item.icon}</NavIcon>
                <NavLabel $collapsed={collapsed}>{item.label}</NavLabel>
              </NavLink>
            </NavItem>
          ))}
        </NavSection>

        {(userRole === "manager" || userRole === "admin") && (
          <NavSection>
            <SectionTitle $collapsed={collapsed}>Gestão</SectionTitle>
            {managementNavItems.map((item) => (
              <NavItem key={item.id}>
                <NavLink
                  $collapsed={collapsed}
                  $active={isActiveRoute(item.path)}
                  onClick={() => handleNavigation(item.path)}
                  title={collapsed ? item.label : undefined}
                >
                  <NavIcon $collapsed={collapsed}>{item.icon}</NavIcon>
                  <NavLabel $collapsed={collapsed}>{item.label}</NavLabel>
                </NavLink>
              </NavItem>
            ))}
          </NavSection>
        )}

        {(userRole === "admin" || user?.role === 'COMPANY_ADMIN') && (
          <NavSection>
            <SectionTitle $collapsed={collapsed}>Administração</SectionTitle>
            {adminNavItems.map((item) => (
              <NavItem key={item.id}>
                <NavLink
                  $collapsed={collapsed}
                  $active={isActiveRoute(item.path)}
                  onClick={() => handleNavigation(item.path)}
                  title={collapsed ? item.label : undefined}
                >
                  <NavIcon $collapsed={collapsed}>{item.icon}</NavIcon>
                  <NavLabel $collapsed={collapsed}>{item.label}</NavLabel>
                </NavLink>
              </NavItem>
            ))}
          </NavSection>
        )}
      </NavigationContainer>

      <QuickActionsContainer>
        <QuickActionButton
          $collapsed={collapsed}
          onClick={() => handleNavigation("/feedback/new")}
        >
          💬 Dar Feedback
        </QuickActionButton>
        <QuickActionButton
          $collapsed={collapsed}
          onClick={() => handleNavigation("/meetings/schedule")}
        >
          📅 Agendar Reunião
        </QuickActionButton>
        {userRole !== "employee" && (
          <>
            <QuickActionButton
              $collapsed={collapsed}
              onClick={() => handleNavigation("/surveys")}
            >
              📊 Nova Pesquisa
            </QuickActionButton>
            <QuickActionButton
              $collapsed={collapsed}
              onClick={() => handleNavigation("/avaliacoes?view=create")}
            >
              📋 Nova Avaliação
            </QuickActionButton>
          </>
        )}
      </QuickActionsContainer>
    </SidebarContainer>
  );
};

export default DashboardSidebar;
